import { BankOutlined, LockOutlined, MailOutlined, PhoneOutlined, SafetyOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Divider, Form, Input, message, Row, Select, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { verificationService } from '../../services/verification';
import './AuthForms.less';

const { Title, Text, Link } = Typography;
const { Option } = Select;

const RegisterForm = ({ onSwitchToLogin, onClose }) => {
  const [form] = Form.useForm();
  const { register, isLoading, error, clearError } = useAuth();
  const [registerError, setRegisterError] = useState(null);
  
  // 验证码相关状态
  const [step, setStep] = useState(1); // 1: 填写信息, 2: 验证邮箱
  const [emailSent, setEmailSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [tempUserData, setTempUserData] = useState(null);

  // 倒计时效果
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 第一步：发送验证码
  const handleSendCode = async (values) => {
    clearError();
    setRegisterError(null);
    setCodeError(null);

    // 保存用户数据
    const userData = {
      email: values.email,
      password: values.password,
      full_name: values.full_name,
      phone: values.phone || null,
      organization: values.organization || null,
      role: 'user'
    };
    setTempUserData(userData);

    // 发送验证码
    setSendingCode(true);
    try {
      const result = await verificationService.sendVerificationCode(
        values.email, 
        values.full_name
      );

      if (result.success) {
        setEmailSent(true);
        setStep(2);
        setCountdown(60); // 60秒倒计时
        message.success(`验证码已发送到 ${values.email}`);
      } else {
        setRegisterError(result.error);
      }
    } catch (error) {
      setRegisterError('发送验证码失败，请稍后重试');
    } finally {
      setSendingCode(false);
    }
  };

  // 第二步：验证验证码并注册
  const handleVerifyAndRegister = async (values) => {
    clearError();
    setCodeError(null);

    if (!tempUserData) {
      setCodeError('用户数据丢失，请重新填写');
      setStep(1);
      return;
    }

    // 验证验证码
    setVerifyingCode(true);
    try {
      const verifyResult = await verificationService.verifyCode(
        tempUserData.email,
        values.verification_code
      );

      if (verifyResult.success) {
        // 验证码正确，进行注册
        const registerResult = await register(tempUserData);
        
        if (registerResult.success) {
          message.success('注册成功！');
          form.resetFields();
          setStep(1);
          setEmailSent(false);
          setTempUserData(null);
          
          if (registerResult.autoLogin) {
            onClose && onClose();
          } else {
            onSwitchToLogin && onSwitchToLogin();
          }
        } else {
          setRegisterError(registerResult.error);
        }
      } else {
        setCodeError(verifyResult.error);
      }
    } catch (error) {
      setCodeError('验证失败，请稍后重试');
    } finally {
      setVerifyingCode(false);
    }
  };

  // 重新发送验证码
  const handleResendCode = async () => {
    if (!tempUserData) return;

    setSendingCode(true);
    try {
      const result = await verificationService.resendVerificationCode(
        tempUserData.email,
        tempUserData.full_name
      );

      if (result.success) {
        setCountdown(60);
        message.success('验证码已重新发送');
      } else {
        message.error(result.error);
      }
    } catch (error) {
      message.error('重新发送失败');
    } finally {
      setSendingCode(false);
    }
  };

  // 返回上一步
  const handleBackToStep1 = () => {
    setStep(1);
    setEmailSent(false);
    setCodeError(null);
    setCountdown(0);
    form.setFieldsValue(tempUserData);
  };

  const handleFieldChange = () => {
    // 清除错误信息当用户开始输入时
    if (registerError) {
      setRegisterError(null);
    }
    if (error) {
      clearError();
    }
    if (codeError) {
      setCodeError(null);
    }
  };

  // 预定义的机构类型
  const organizationTypes = [
    '三甲医院',
    '二甲医院',
    '一甲医院',
    '专科医院',
    '社区医院',
    '私立医院',
    '体检中心',
    '第三方检验机构',
    '科研院所',
    '医学院校',
    '其他'
  ];

  // 渲染步骤指示器
  const renderStepIndicator = () => (
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <Space size="large">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          color: step >= 1 ? '#1890ff' : '#d9d9d9'
        }}>
          <div style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: step >= 1 ? '#1890ff' : '#d9d9d9',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            marginRight: 8
          }}>
            1
          </div>
          <Text style={{ color: step >= 1 ? '#1890ff' : '#d9d9d9' }}>
            填写信息
          </Text>
        </div>
        
        <div style={{ 
          width: 40, 
          height: 1, 
          backgroundColor: step >= 2 ? '#1890ff' : '#d9d9d9' 
        }} />
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          color: step >= 2 ? '#1890ff' : '#d9d9d9'
        }}>
          <div style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: step >= 2 ? '#1890ff' : '#d9d9d9',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            marginRight: 8
          }}>
            2
          </div>
          <Text style={{ color: step >= 2 ? '#1890ff' : '#d9d9d9' }}>
            邮箱验证
          </Text>
        </div>
      </Space>
    </div>
  );

  return (
    <div className="auth-form">
      <div className="auth-form-header">
        <Title level={3}>用户注册</Title>
        <Text type="secondary">
          {step === 1 ? '创建您的 MedSyntix 账户' : '验证您的邮箱地址'}
        </Text>
      </div>

      {renderStepIndicator()}

      {(error || registerError || codeError) && (
        <Alert
          message={error || registerError || codeError}
          type="error"
          showIcon
          closable
          onClose={() => {
            clearError();
            setRegisterError(null);
            setCodeError(null);
          }}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        name="register"
        onFinish={step === 1 ? handleSendCode : handleVerifyAndRegister}
        onFieldsChange={handleFieldChange}
        autoComplete="off"
        size="large"
        layout="vertical"
        scrollToFirstError
      >
        {step === 1 ? (
          // 第一步：填写基本信息
          <>
            <Form.Item
          name="email"
          label="邮箱地址"
          rules={[
            { required: true, message: '请输入邮箱地址' },
            { type: 'email', message: '请输入有效的邮箱地址' }
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="请输入邮箱地址"
            autoComplete="email"
          />
        </Form.Item>

        <Form.Item
          name="full_name"
          label="姓名"
          rules={[
            { required: true, message: '请输入您的姓名' },
            { min: 2, message: '姓名至少2个字符' },
            { max: 50, message: '姓名不能超过50个字符' }
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="请输入您的姓名"
            autoComplete="name"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="密码"
          rules={[
            { required: true, message: '请输入密码' },
            { min: 6, message: '密码至少6位字符' },
            { max: 128, message: '密码不能超过128位字符' },
            {
              pattern: /^(?=.*[a-zA-Z])(?=.*\d)/,
              message: '密码必须包含字母和数字'
            }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="请输入密码（包含字母和数字）"
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="确认密码"
          dependencies={['password']}
          rules={[
            { required: true, message: '请确认密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('两次输入的密码不一致'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="请再次输入密码"
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label="手机号码（可选）"
          rules={[
            {
              pattern: /^1[3-9]\d{9}$/,
              message: '请输入有效的手机号码'
            }
          ]}
        >
          <Input
            prefix={<PhoneOutlined />}
            placeholder="请输入手机号码"
            autoComplete="tel"
          />
        </Form.Item>

        <Form.Item
          name="organization"
          label="所属机构（可选）"
        >
          <Select
            placeholder="请选择或输入您的所属机构"
            allowClear
            showSearch
            optionFilterProp="children"
            dropdownRender={(menu) => (
              <div>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ padding: '8px', color: '#999', fontSize: '12px' }}>
                  可以选择预设选项或输入自定义机构名称
                </div>
              </div>
            )}
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {organizationTypes.map(type => (
              <Option key={type} value={type}>
                <BankOutlined style={{ marginRight: 8 }} />
                {type}
              </Option>
            ))}
          </Select>
        </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={sendingCode}
                block
                size="large"
              >
                {sendingCode ? '发送中...' : '发送验证码'}
              </Button>
            </Form.Item>
          </>
        ) : (
          // 第二步：验证邮箱
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <MailOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
              <Title level={4}>验证您的邮箱</Title>
              <Text type="secondary">
                我们已向 <Text strong>{tempUserData?.email}</Text> 发送验证码
              </Text>
            </div>

            <Form.Item
              name="verification_code"
              label="邮箱验证码"
              rules={[
                { required: true, message: '请输入验证码' },
                { len: 6, message: '验证码为6位数字' },
                { pattern: /^\d{6}$/, message: '验证码只能是数字' }
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="请输入6位验证码"
                maxLength={6}
                style={{ 
                  textAlign: 'center',
                  letterSpacing: '0.2em',
                  fontSize: '18px'
                }}
                autoComplete="one-time-code"
              />
            </Form.Item>

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Text type="secondary">
                没有收到验证码？
              </Text>
              {countdown > 0 ? (
                <Text type="secondary">
                  {countdown}秒后可重新发送
                </Text>
              ) : (
                <Button 
                  type="link" 
                  size="small"
                  loading={sendingCode}
                  onClick={handleResendCode}
                  style={{ padding: 0, marginLeft: 8 }}
                >
                  重新发送
                </Button>
              )}
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Button
                  block
                  size="large"
                  onClick={handleBackToStep1}
                >
                  返回修改
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={verifyingCode}
                  block
                  size="large"
                >
                  {verifyingCode ? '验证中...' : '验证并注册'}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Form>

      <Divider plain>
        <Text type="secondary">已有账户？</Text>
      </Divider>

      <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
        <Link onClick={onSwitchToLogin}>
          立即登录
        </Link>
        
        <Text type="secondary" style={{ fontSize: '12px' }}>
          注册即表示您同意我们的
          <Link style={{ fontSize: '12px' }}>服务条款</Link>
          和
          <Link style={{ fontSize: '12px' }}>隐私政策</Link>
        </Text>
      </Space>
    </div>
  );
};

export default RegisterForm; 